// @ts-nocheck
import {
  ICreateTeamRequest,
  IInviteMemberRequest,
  ITeam,
  ITeamMembers,
  ITeamNotificationUser,
  IUpdateTeamRequest,
  TeamMembersRoles,
} from "@/integration/team/interfaces/team.interface.ts";
import { BaseResponse } from "@/integration/application/interfaces/base.ts";
import { ISearchPagination } from "@/integration/application/interfaces/searchPagination.interface.ts";
import { IGameDTO } from "@/integration/game/interfaces/game.interface.ts";
import apiClient from "@/integration/factory/api-client.ts";
import { ISendInviteUserToTeamRequest, ITeamPerformanceOverview } from "@integration/team/interfaces/ITeam.ts";

/**
 * Service for team-related API calls
 * Refactored to use the ApiClient for better error handling and request cancellation
 */
export default function teamService() {
  const postCreate = async (data: ICreateTeamRequest): Promise<ITeam> => {
    return await apiClient.post("/teams", data);
  };

  const putUpdate = async (
    teamId: string,
    data: IUpdateTeamRequest
  ): Promise<ITeam> => {
    return await apiClient.put(`/teams/${teamId}`, {id:teamId, ...data});
  };

  const getProfileTeam = async (
    teamId: string
  ): Promise<BaseResponse<ITeam>> => {
    if (!teamId) {
      throw new Error('Team ID cannot be null or undefined');
    }
    return await apiClient.get(`/teams/${teamId}`);
  };

  const postSendInvite = async (teamId: string, data: IInviteMemberRequest) => {
    return await apiClient.post(`/teams/${teamId}/invite`, data);
  };

  const postSendInvites = async (teamId: string, data: IInviteMemberRequest[]) => {
    return await apiClient.post(`/teams/${teamId}/invites`, data);
  };

  const deleteTeamMemberByUserId = async (id: string, teamMemberId: string) => {
    return await apiClient.delete(`/teams/${id}/team-member/${teamMemberId}`);
  };

  const patchRoleTeamMember = async (
    teamId: string,
    teamMemberId: string,
    newRole: TeamMembersRoles
  ) => {
    return await apiClient.patch(
      `/teams/${teamId}/team-member/${teamMemberId}`, {newRole}
    );
  };

  const getInviteNotificationsUser = async (): Promise<
    ITeamNotificationUser[]
  > => {
    return await apiClient.get("/teams/invitations/user");
  };

  const getTeamInvitations = async (
    teamId: string,
    filters?: { status?: string; page?: number; limit?: number; }
  ): Promise<ITeamNotificationUser[]> => {
    const queryParams = filters ? 
      Object.entries(filters)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => `${key}=${value}`)
        .join('&') 
      : '';

    const url = `/teams/${teamId}/invitations${queryParams ? `?${queryParams}` : ''}`;
    return await apiClient.get(url);
  };

  const patchAcceptionInvitation = async (
    teamId: string,
    invitationId: string
  ) => {
    return await apiClient.patch(
      `/teams/${teamId}/invitations/${invitationId}/accept`
    );
  };

  const postInviteUserToTeam = async (
    teamId: string,
    request: ISendInviteUserToTeamRequest
  ) => {
    return await apiClient.patch(
      `/teams/${teamId}/applyToTeam`, request
    );
  };

  const patchDeclineInvitation = async (
    teamId: string,
    invitationId: string
  ) => {
    return await apiClient.patch(
      `/teams/${teamId}/invitations/${invitationId}/decline`
    );
  };

  const getTeamMembers = async (
    teamId: string
  ): Promise<BaseResponse<ITeamMembers>[]> => {
    return await apiClient.get(`teams/${teamId}/members`);
  };

  const getGamesTeam = async (
    teamId: string
  ): Promise<IGameDTO[]> => {
    return await apiClient.get(`teams/${teamId}/games`);
  };

  const searchTeamByNameAndTag = async (searchPagination: ISearchPagination
  ): Promise<BaseResponse<ITeam>[]> => {
    const query = [
      `search=${searchPagination.search}`,
      `page=${searchPagination.page}`,
      `limit=${searchPagination.limit}`,
      `order=${searchPagination.order}`,
      searchPagination.entityId ? `entityId=${searchPagination.entityId}` : null
    ]
      .filter(Boolean)
      .join("&");

    return await apiClient.get(`public/teams/options/search?${query}`);
  };

  const searchMyTeamsByNameAndTag = async (searchPagination: ISearchPagination
  ): Promise<BaseResponse<ITeam>[]> => {
    const query = [
      `search=${searchPagination.search}`,
      `page=${searchPagination.page}`,
      `limit=${searchPagination.limit}`,
      `order=${searchPagination.order}`,
      searchPagination.gameId ? `gameId=${searchPagination.gameId}` : null,
      searchPagination.entityId ? `entityId=${searchPagination.entityId}` : null
    ]
      .filter(Boolean)
      .join("&");

    return await apiClient.get(`/teams/options/user/search?${query}`);
  };

  const getTeamByIdPublic = async (
    teamId: string
  ): Promise<BaseResponse<ITeam>> => {
    return await apiClient.get(`public/teams/${teamId}`);
  };

  const getStatsTeamByIdPublic = async (
    teamId: string
  ): Promise<ITeamPerformanceOverview | null> => {
    try {
      return await apiClient.get(`public/teams/${teamId}/stats`, {
        params: { businessType: 'TEAM' }
      });
    } catch (error) {
      return null;
    }
  };


  const getTeamMembersByIdPublic = async (
    teamId: string
  ): Promise<BaseResponse<ITeamMembers>[]> => {
    return await apiClient.get(`public/teams/${teamId}/members`);
  };

  const getTeamChampionshipRegistrations = async (
    teamId: string,
    page: number = 1,
    limit: number = 10
  ) => {
    try {
      if (!teamId) {
        throw new Error('Team ID cannot be null or undefined');
      }

      return await apiClient.get(`/championships/registration/team/${teamId}/paginated`, {
        params: { page, limit }
      });
    } catch (error) {
      console.error("Error getting team championship registrations:", error);
      // Return a default paginated response
      return { data: [], page, limit, total: 0, totalPage: 0 };
    }
  };

  return {
    postCreate,
    getProfileTeam,
    postSendInvite,
    postSendInvites,
    getInviteNotificationsUser,
    getTeamInvitations,
    patchAcceptionInvitation,
    patchDeclineInvitation,
    getTeamMembers,
    getTeamByIdPublic,
    getTeamMembersByIdPublic,
    putUpdate,
    searchTeamByNameAndTag,
    deleteTeamMemberByUserId,
    patchRoleTeamMember,
    getGamesTeam,
    getStatsTeamByIdPublic,
    searchMyTeamsByNameAndTag,
    postInviteUserToTeam,
    getTeamChampionshipRegistrations
  };
}
